"use client";

import { useCallback } from "react";
import { useLenis } from "lenis/react";
import { useReducedMotion } from "framer-motion";
import { resolveAnchorTarget } from "@/utils/anchorId";
import {
  findScrollTargetElement,
  type FindScrollTargetOptions,
} from "@/utils/scrollAnchor";

/** Clears the sticky landing header when anchoring (Lenis ignores CSS scroll-margin). */
const ANCHOR_SCROLL_OFFSET = -96;

export type ScrollToAnchorOptions = FindScrollTargetOptions;

/**
 * Returns a callback that smooth-scrolls to an in-page anchor (`#id`). Prefers
 * Lenis when mounted and falls back to native `scrollIntoView`.
 */
export function useScrollToAnchor() {
  const lenis = useLenis();
  const shouldReduceMotion = useReducedMotion();

  return useCallback(
    (href: string, options?: ScrollToAnchorOptions) => {
      const anchorId = resolveAnchorTarget(href);
      if (!anchorId) return;

      const target = findScrollTargetElement(anchorId, options);
      if (!target) return;

      if (lenis) {
        lenis.scrollTo(target as HTMLElement, {
          immediate: shouldReduceMotion ?? false,
          offset: ANCHOR_SCROLL_OFFSET,
        });
        return;
      }

      target.scrollIntoView({
        behavior: shouldReduceMotion ? "auto" : "smooth",
        block: "start",
      });
    },
    [lenis, shouldReduceMotion],
  );
}
